import { getAnalyticsUserId } from "./analyticsIdentity";
import {
    persistProfileReferralForSignup,
    trackProfileLinkJoined,
} from "./frontendTrackingEvents";

const DEDUPE_STORAGE_PREFIX = "c8_profile_link_joined:";

function parseSearchParams(search: string): URLSearchParams {
    const raw = search.startsWith("?") ? search.slice(1) : search;
    return new URLSearchParams(raw);
}

function normalizePathname(pathname: string): string {
    const trimmed = (pathname || "/").trim();
    if (!trimmed || trimmed === "/") return "/";
    const withoutTrailing = trimmed.replace(/\/+$/, "");
    return withoutTrailing || "/";
}

function decodeBackParam(back: string): { pathname: string; search: string } | null {
    let decoded = back.trim();
    if (!decoded) return null;
    try {
        decoded = decodeURIComponent(decoded);
    } catch {
        /* use raw */
    }
    const withSlash = decoded.startsWith("/") ? decoded : `/${decoded}`;
    const qIndex = withSlash.indexOf("?");
    if (qIndex === -1) return { pathname: normalizePathname(withSlash), search: "" };
    return {
        pathname: normalizePathname(withSlash.slice(0, qIndex)),
        search: withSlash.slice(qIndex),
    };
}

/** `p` (player id) or legacy `u` (user id / username) from a shareable `/profile` URL. */
export function parsePublicProfileLinkSenderId(pathname: string, search: string): string | null {
    const path = normalizePathname(pathname);
    if (path !== "/profile") return null;
    const params = parseSearchParams(search);
    if (params.get("edit") === "1") return null;
    const p = params.get("p")?.trim();
    const u = params.get("u")?.trim();
    return p || u || null;
}

function resolvePublicProfileLinkTarget(
    pathname: string,
    search: string
): { pathname: string; search: string } | null {
    const senderId = parsePublicProfileLinkSenderId(pathname, search);
    if (senderId) {
        return { pathname: normalizePathname(pathname), search };
    }
    const back = parseSearchParams(search).get("back");
    if (!back) return null;
    const fromBack = decodeBackParam(back);
    if (!fromBack) return null;
    return parsePublicProfileLinkSenderId(fromBack.pathname, fromBack.search) ? fromBack : null;
}

export type ProfileLinkViewerUser = {
    playerId?: string | null;
    userId?: string | number | null;
    username?: string | null;
};

export function isOwnPublicProfileLink(senderId: string, user?: ProfileLinkViewerUser | null): boolean {
    if (!senderId?.trim() || !user) return false;
    const sid = senderId.trim();
    const pid = (user.playerId ?? "").trim();
    if (pid && pid === sid) return true;
    if (user.userId != null && String(user.userId) === sid) return true;
    const uname = (user.username ?? "").trim();
    if (uname && uname === sid) return true;
    return false;
}

function buildProfileLinkDedupeKey(senderId: string): string {
    return senderId.trim();
}

function hasTrackedProfileLinkJoin(dedupeKey: string): boolean {
    try {
        return sessionStorage.getItem(`${DEDUPE_STORAGE_PREFIX}${dedupeKey}`) === "1";
    } catch {
        return false;
    }
}

function markProfileLinkJoinTracked(dedupeKey: string): void {
    try {
        sessionStorage.setItem(`${DEDUPE_STORAGE_PREFIX}${dedupeKey}`, "1");
    } catch {
        /* ignore */
    }
}

/**
 * Fire `profile_link_joined` once per tab per profile link (recipient opened a shared `/profile` URL).
 * Also persists referral for `signup_started` / `signup_completed` attribution.
 */
export function tryTrackProfileLinkJoined({
    pathname,
    search,
    user_id,
    viewer,
}: {
    pathname: string;
    search: string;
    user_id?: string | number | null;
    viewer?: ProfileLinkViewerUser | null;
}): void {
    const target = resolvePublicProfileLinkTarget(pathname, search);
    if (!target) return;
    const senderId = parsePublicProfileLinkSenderId(target.pathname, target.search);
    if (!senderId || isOwnPublicProfileLink(senderId, viewer)) return;

    const dedupeKey = buildProfileLinkDedupeKey(senderId);
    if (hasTrackedProfileLinkJoin(dedupeKey)) return;
    markProfileLinkJoinTracked(dedupeKey);

    persistProfileReferralForSignup(senderId);

    const uid = user_id ?? getAnalyticsUserId();
    trackProfileLinkJoined({
        user_id: uid != null && String(uid).trim() !== "" ? uid : undefined,
        sender_user_id: senderId,
        invite_method: "link",
        pathname: pathname || (typeof window !== "undefined" ? window.location.pathname : "/profile"),
    });
}

/** In-app profile modal open (ProfileLink) — same event + dedupe as URL landing. */
export function tryTrackProfileLinkJoinedFromModalOpen({
    p,
    u,
    viewer,
    user_id,
    pathname,
}: {
    p?: string | null;
    u?: string | null;
    viewer?: ProfileLinkViewerUser | null;
    user_id?: string | number | null;
    pathname?: string;
}): void {
    const senderId = (p || u || "").trim();
    if (!senderId || isOwnPublicProfileLink(senderId, viewer)) return;

    const dedupeKey = buildProfileLinkDedupeKey(senderId);
    if (hasTrackedProfileLinkJoin(dedupeKey)) return;
    markProfileLinkJoinTracked(dedupeKey);

    persistProfileReferralForSignup(senderId);

    const uid = user_id ?? getAnalyticsUserId();
    trackProfileLinkJoined({
        user_id: uid != null && String(uid).trim() !== "" ? uid : undefined,
        sender_user_id: senderId,
        invite_method: "link",
        pathname: pathname || (typeof window !== "undefined" ? window.location.pathname : "/"),
    });
}
